// @flow

import * as React from 'react';
import { css } from '@emotion/core';
import moment from 'moment';
import type {Airing, Episode, Show} from "../../Objects";
import New from "../New";
import {visuallyHidden} from "../../../Styles/accessibility";
import type {ExpansionConfig} from "./ScheduleGrid";
import {fontSize} from "../../../Styles/text";
import colors from "../../../Styles/colors";
import {channelDisplayName} from "../../helpers";

const entry = css`
  width: 100%;
  height: 100%;
  padding: 4px;
  box-shadow:0 0 0 1px #ccc;
  ${fontSize(5)};
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  cursor: pointer;
  scroll-snap-align: end;
  
  &:hover {
    background: #bfe7f6;
  }
  
  @media print {
    border: 1px solid ${colors.coreColor1};
  }
`;

const episodeInfo = css`
  ${fontSize(6)};
`;

function forKeys(keys: Array<any>, action: Function): SyntheticKeyboardEvent<any> => any {
  return e => {
    if (keys.indexOf(e.key) !== -1) {
      e.preventDefault();
      action(e);
    }
  }
}

type Props = {|
  airings: Array<number>,
  msRows: Map<string, number>,
  msCols: Map<string, number>,
  getAiring: number => Airing | null,
  getEpisode: number => Episode | null,
  getShow: number => Show | null,
  firstCellRef: ?{ current: null | React.ElementRef<any> },
  returnRef: { current: null | React.ElementRef<any> },
  keyboardEnabled: bool,
  expansion: ExpansionConfig,
  clickFactory: (string, number, number) => Function
|};

export default React.memo<Props>(function ScheduleChannel(props: Props) {
  let {
    airings,
    msRows,
    msCols,
    getAiring,
    getEpisode,
    getShow,
    firstCellRef,
    keyboardEnabled,
    expansion,
    clickFactory,
    returnRef
  } = props;

  if (airings.length === 0) {
    return [];
  }

  let gridEntries = [];
  let currentAiringId = airings[0];
  let startPos = 0;
  let endPos = 0;

  for (let i = 0; i <= airings.length; i++) {
    let airingId = airings[i];

    if (currentAiringId === airingId) {
      endPos += 1;
    } else {
      let airing = getAiring(currentAiringId);

      if (airing) {
        let episode = getEpisode(airing.episode);
        let show = getShow(airing.show);

        if (episode && show) {
          // $FlowFixMe - Calls to get here are guaranteed, assuming the invariants of getGrid hold
          let msGridRowSpan = airings.length * 2 + 8 - msRows.get(`time-${startPos}`);

          if (msRows.get(`time-${endPos}`)) {
            // $FlowFixMe - Calls to get here are guaranteed, assuming the invariants of getGrid hold
            msGridRowSpan = msRows.get(`time-${endPos}`) - msRows.get(`time-${startPos}`);
          }

          let style = {
            gridRowStart: `time-${startPos}`,
            gridRowEnd: `time-${endPos}`,
            gridColumnStart: `channel-${airing.channel}`,
            msGridRow: msRows.get(`time-${startPos}`),
            msGridRowSpan,
            msGridColumn: msCols.get(`channel-${airing.channel}`),
            msGridColumnSpan: 1,
          };

          if (!keyboardEnabled) {
            // $FlowFixMe - This is expected to be potentially undefined
            style.outline = 'none';
          }

          if (expansion && expansion.airingId === currentAiringId) {
            // $FlowFixMe - This is expected to be potentially undefined
            style.background = '#bfe7f6';
          }

          let ref = null;

          if (startPos === 0 && firstCellRef != null) {
            ref = firstCellRef;
          } else if (expansion && expansion.airingId === currentAiringId) {
            ref = returnRef;
          }

          let diff = airing.date - episode.orig_broadcast_date;
          let isNew = diff >= 0 && diff < 24 * 60 * 60;

          // Build out a reader formatted time to place in the entry cell for
          // screen readers. We break out individual parts with spaces to try
          // and force the reader to read the individual parts instead of trying
          // to pronounce words: A M versus am
          let formatter = moment(airing.date * 1000).tz('America/Chicago');

          let readerParts = [];
          let readerMinutes = formatter.format('mm');
          let readerHour = formatter.format('h');
          let readerAMPM = formatter.format('A').split('').join(' ');

          readerParts.push(readerHour);

          if (parseInt(readerMinutes) > 0) {
            readerParts.push(':');
            readerParts.push(readerMinutes);
          }

          readerParts.push(' ');
          readerParts.push(readerAMPM);

          gridEntries.push(<div
            key={currentAiringId}
            css={entry}
            style={style}
            ref={ref}
            tabIndex={0}
            onClick={clickFactory(show.title, currentAiringId, startPos)}
            onKeyDown={forKeys(['Enter', ' '], clickFactory(show.title, currentAiringId, startPos))}
          >
            <span css={visuallyHidden}>
              {`${readerParts.join('')} on ${channelDisplayName(airing.channel)}`}
            </span>
            <div><b>{show.title}</b></div>
            <div css={episodeInfo}>
              { isNew && <New/> }
              {
                episode.number !== 0 && `#${episode.number}`
              }
              {
                // We explicitly want to ignore the empty string here
                // $FlowFixMe
                episode.title && ` - ${episode.title}`
              }
            </div>
          </div>);
        }
      }

      startPos = endPos;
      endPos++;
      currentAiringId = airingId;
    }
  }

  return gridEntries;
})